import { apiCustomer } from "@/api"
import { activeOrdersAtom, recentOrdersAtom } from "@/atoms/active-orders"
import profileAtom from "@/atoms/profile"
import { getMerchantId } from "@/lib/company-storage"
import { ordersByCustomerQuery, sortOrdersByCreatedAtDesc } from "@/lib/order-history"
import { isOrderInProgress } from "@/lib/order-status"
import { OrderResponse } from "@/types/external"
import { useAtomValue, useSetAtom } from "jotai"
import { useCallback, useEffect, useRef } from "react"
import { useAuthStatus } from "./use-auth-status"

const POLL_INTERVAL_MS = 30_000

/**
 * Module-level handle to the latest `fetchOrders` function so any component
 * (e.g. checkout) can force an immediate refresh after creating an order
 * without waiting for the next 30s polling tick.
 */
let activeFetchRef: (() => Promise<void>) | null = null

/**
 * Triggers an immediate refetch of the customer's active orders.
 * No-op if `useActiveOrders` is not mounted yet.
 */
export function refreshActiveOrders(): void {
  if (activeFetchRef) {
    void activeFetchRef()
  }
}

/**
 * Polls the customer's orders for the current merchant and keeps
 * `activeOrdersAtom` and `recentOrdersAtom` in sync.
 *
 * Contract:
 * - Only runs when the user is authenticated and a profile is loaded.
 * - Pauses automatically when the tab is not visible.
 * - Fails silently: a network error doesn't break the UI.
 */
export function useActiveOrders() {
  const isAuthenticated = useAuthStatus()
  const profile = useAtomValue(profileAtom)
  const setActiveOrders = useSetAtom(activeOrdersAtom)
  const setRecentOrders = useSetAtom(recentOrdersAtom)

  const setActiveOrdersRef = useRef(setActiveOrders)
  const setRecentOrdersRef = useRef(setRecentOrders)
  setActiveOrdersRef.current = setActiveOrders
  setRecentOrdersRef.current = setRecentOrders

  const fetchOrders = useCallback(async () => {
    const merchantId = getMerchantId()
    if (!merchantId) {
      console.info("[useActiveOrders] no merchantId in localStorage")
      return
    }

    try {
      const { data } = await apiCustomer.get<OrderResponse[]>(
        `/orders/merchants/${merchantId}/by-customer${ordersByCustomerQuery}`,
      )

      const list = sortOrdersByCreatedAtDesc(data)
      const inProgress = list.filter(isOrderInProgress)
      const recentTerminal = list.filter((o) => !isOrderInProgress(o)).slice(0, 5)

      console.info("[useActiveOrders] fetched", {
        merchantId,
        total: list.length,
        inProgress: inProgress.length,
        recentTerminal: recentTerminal.length,
        lastOrderId: list[0]?.id ?? null,
      })

      setActiveOrdersRef.current(inProgress)
      setRecentOrdersRef.current(recentTerminal)
    } catch (err) {
      console.info("[useActiveOrders] fetch failed (silent)", err)
    }
  }, [])

  useEffect(() => {
    if (isAuthenticated && profile) return
    setActiveOrders([])
    setRecentOrders([])
  }, [isAuthenticated, profile, setActiveOrders, setRecentOrders])

  useEffect(() => {
    activeFetchRef = fetchOrders
    if (!isAuthenticated || !profile) {
      console.info("[useActiveOrders] not polling", {
        isAuthenticated,
        hasProfile: !!profile,
      })
      return
    }

    let intervalId: number | undefined

    const start = () => {
      fetchOrders()
      intervalId = window.setInterval(fetchOrders, POLL_INTERVAL_MS)
    }

    const stop = () => {
      if (intervalId !== undefined) {
        window.clearInterval(intervalId)
        intervalId = undefined
      }
    }

    const handleVisibility = () => {
      if (document.visibilityState === "visible") {
        if (intervalId === undefined) start()
        else fetchOrders()
      } else {
        stop()
      }
    }

    if (document.visibilityState === "visible") {
      start()
    }
    document.addEventListener("visibilitychange", handleVisibility)

    return () => {
      stop()
      if (activeFetchRef === fetchOrders) activeFetchRef = null
      document.removeEventListener("visibilitychange", handleVisibility)
    }
  }, [isAuthenticated, profile, fetchOrders])

  return { refresh: fetchOrders }
}
